export function isPlaceholderProduct(product) {
  return Boolean(product?.hasOnlyDefaultVariant) && Number(product?.price) === 0;
}

export function guardPlaceholderSubmit(event, placeholder) {
  if (!placeholder) return true;
  event?.preventDefault?.();
  event?.stopImmediatePropagation?.();
  return false;
}

export function variantAnnouncement(variant) {
  if (!variant) return '';
  const parts = [variant.title, variant.formattedPrice, variant.available ? 'available' : 'sold out'];
  return parts.filter(Boolean).join(', ');
}

export function enforceSingleAccordion(opened, items, singleOpen) {
  if (!singleOpen || !opened?.open) return;
  items.forEach((item) => { if (item !== opened) item.open = false; });
}

export function syncAccordionExpandedState(items) {
  items.forEach((item) => {
    item.querySelector?.('summary')?.setAttribute?.('aria-expanded', String(Boolean(item.open)));
  });
}

export function pauseOtherVideos(active, videos) {
  videos.forEach((video) => { if (video !== active) video.pause?.(); });
}

export function createInitializer(factory) {
  const instances = new WeakMap();
  return (root) => {
    if (!root) return null;
    if (!instances.has(root)) instances.set(root, factory(root));
    return instances.get(root);
  };
}

export function createVariantHistoryUrl(currentUrl, variantId) {
  const url = new URL(currentUrl, typeof window === 'undefined' ? 'https://example.invalid' : window.location.origin);
  url.search = '';
  url.searchParams.set('variant', String(variantId));
  url.hash = '';
  return url.toString();
}

export function variantIdFromUrl(url) {
  try {
    return new URL(url, typeof window === 'undefined' ? 'https://example.invalid' : window.location.origin)
      .searchParams.get('variant') || '';
  } catch {
    return '';
  }
}

export function commitVariantHistory(historyApi, previousUrl, nextUrl) {
  if (!historyApi?.replaceState || !historyApi?.pushState || !previousUrl || !nextUrl || previousUrl === nextUrl) return false;
  historyApi.replaceState({ path: previousUrl }, '', previousUrl);
  historyApi.pushState({ path: nextUrl }, '', nextUrl);
  return true;
}

export function applyVariantOptions(root, variant) {
  if (!root || !Array.isArray(variant?.options)) return false;
  const inputs = [...root.querySelectorAll('input[type="radio"]')];
  const groups = [...new Set(inputs.map((input) => input.name))];
  const matches = variant.options.map((value, index) => inputs.find((input) => (
    input.dataset?.index === `option${index + 1}` || input.name === groups[index]
  ) && input.value === value));
  if (matches.some((input) => !input)) return false;
  const changed = matches.filter((input) => !input.checked);
  matches.forEach((input) => { input.checked = true; });
  (changed.at(-1) || matches.at(-1)).dispatchEvent(new Event('change', { bubbles: true }));
  return true;
}

export function getVariantEventSource(root) {
  return root?.querySelector?.('.product-section') || root;
}

export function restoreZoomFocus(trigger) {
  if (trigger?.isConnected) trigger.focus?.({ preventScroll: true });
}

export function applyRecommendationResult(host, result) {
  const target = host?.querySelector?.('[data-wq-recommendation-content]');
  if (!host || !target || !result?.html || Number(result.count) < 1) {
    host?.classList?.add?.('hide');
    return false;
  }
  target.innerHTML = result.html;
  host.classList.remove('hide');
  return true;
}

export async function requestRecommendationMarkup(url, { fetchImpl = fetch, parseImpl, signal } = {}) {
  try {
    const response = await fetchImpl(url, { signal });
    if (!response?.ok) return null;
    const text = await response.text();
    return parseImpl?.(text) || null;
  } catch (error) {
    if (error?.name === 'AbortError') return null;
    return null;
  }
}

export function createRecommendationRequestOnce(loader) {
  const requests = new WeakMap();
  return (target) => {
    if (!requests.has(target)) requests.set(target, Promise.resolve().then(() => loader(target)));
    return requests.get(target);
  };
}

const recommendationRequests = new WeakMap();

export function loadRecommendationIntoHost(host, {
  fetchImpl = fetch,
  parseImpl = parseRecommendationHtml,
  signal = host?.abortController?.signal
} = {}) {
  if (!host?.dataset?.url) return Promise.resolve(false);
  if (recommendationRequests.has(host)) return recommendationRequests.get(host);

  const sequence = (host.requestSequence || 0) + 1;
  host.requestSequence = sequence;
  const request = requestRecommendationMarkup(host.dataset.url, { fetchImpl, parseImpl, signal })
    .then((result) => {
      if (sequence !== host.requestSequence || signal?.aborted) return false;
      return applyRecommendationResult(host, result);
    });
  recommendationRequests.set(host, request);
  return request;
}

function parseRecommendationHtml(text) {
  const documentFragment = new DOMParser().parseFromString(text, 'text/html');
  const source = documentFragment.querySelector('wq-product-recommendations [data-wq-recommendation-content]');
  const count = source?.querySelectorAll?.('.wq-product-card').length || 0;
  return count ? { html: source.innerHTML, count } : null;
}

const HTMLElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

class WqProductRecommendations extends HTMLElementBase {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    if (this.querySelector?.('.wq-product-card')) {
      this.classList.remove('hide');
      return;
    }
    this.abortController = new AbortController();
    this.requestSequence = this.requestSequence || 0;
    this.load();
  }

  async load() {
    const rendered = await loadRecommendationIntoHost(this, { signal: this.abortController.signal });
    if (!rendered) return;
    window.theme?.reinitProductGridItem?.(this);
    document.dispatchEvent(new CustomEvent('recommendations:loaded', { detail: { section: this } }));
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.requestSequence = (this.requestSequence || 0) + 1;
    recommendationRequests.delete(this);
    this.initialized = false;
  }
}

const focusableSelector = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function observeProductVideoSpacing(root, Observer = globalThis.ResizeObserver, Mutation = globalThis.MutationObserver) {
  if (!Observer) return null;
  const observer = new Observer((entries) => {
    entries.forEach(({ target, contentRect }) => {
      target.parentElement?.classList.toggle('wq-video-has-content', contentRect.height > 0);
    });
  });
  const observed = new WeakSet();
  const discover = () => root.querySelectorAll('fw-embed-feed').forEach((feed) => {
    if (observed.has(feed)) return;
    observed.add(feed);
    observer.observe(feed);
  });
  discover();
  const mutationObserver = Mutation ? new Mutation(discover) : null;
  mutationObserver?.observe(root, { childList: true, subtree: true });
  return { disconnect() { observer.disconnect(); mutationObserver?.disconnect(); } };
}

function handleAccordionToggle(holder, event) {
  const item = event.target;
  if (!item?.matches?.('[data-wq-accordion-item]')) return;
  if (item.closest?.('[data-wq-accordion-root]') !== holder) return;
  const items = [...holder.querySelectorAll('[data-wq-accordion-item]')];
  enforceSingleAccordion(item, items, holder.dataset.allowMultiple !== 'true');
  syncAccordionExpandedState(items);
}

class WqProductController {
  constructor(root) {
    this.root = root;
    this.abortController = new AbortController();
    this.signal = this.abortController.signal;
    this.placeholder = root.dataset.wqPlaceholder === 'true' || root.hasAttribute('data-wq-placeholder-product');
    this.live = root.querySelector?.('[data-wq-product-live]');
    this.onSubmit = this.onSubmit.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.onVideoPlay = this.onVideoPlay.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onPopState = this.onPopState.bind(this);
    this.lastVariantUrl = window.location.href;
    this.restoringVariantHistory = false;
    root.addEventListener?.('submit', this.onSubmit, { capture: true, signal: this.signal });
    this.variantEventSource = getVariantEventSource(root);
    this.variantEventSource?.addEventListener?.('variantChange', this.onVariantChange, { signal: this.signal });
    root.addEventListener?.('toggle', this.onToggle, { capture: true, signal: this.signal });
    root.addEventListener?.('play', this.onVideoPlay, { capture: true, signal: this.signal });
    root.addEventListener?.('click', this.onClick, { signal: this.signal });
    root.addEventListener?.('keydown', this.onKeydown, { signal: this.signal });
    window.addEventListener?.('popstate', this.onPopState, { signal: this.signal });
    this.observeVideos();
    this.productVideoSpacingObserver = observeProductVideoSpacing(root);
    this.observePhotoSwipe();
  }

  onSubmit(event) {
    guardPlaceholderSubmit(event, this.placeholder || Boolean(event.target?.closest?.('[data-wq-placeholder-product]')));
  }

  onVariantChange(event) {
    const variant = event.detail?.variant;
    if (!variant) return;
    queueMicrotask(() => {
      const nextUrl = createVariantHistoryUrl(window.location.href, variant.id);
      if (this.restoringVariantHistory) {
        this.lastVariantUrl = nextUrl;
        return;
      }
      commitVariantHistory(window.history, this.lastVariantUrl, nextUrl);
      this.lastVariantUrl = nextUrl;
    });
    if (!this.live) return;
    requestAnimationFrame(() => {
      const formattedPrice = this.root.querySelector?.('[data-product-price]')?.textContent?.trim();
      this.live.textContent = variantAnnouncement({ title: variant.public_title || variant.title, available: variant.available, formattedPrice });
    });
  }

  onPopState() {
    const variantId = variantIdFromUrl(window.location.href);
    if (!variantId) return;
    const json = this.root.querySelector?.('[data-variant-json]')?.value;
    if (!json) return;
    let variants;
    try { variants = JSON.parse(json); } catch { return; }
    const variant = variants.find((item) => String(item.id) === variantId);
    if (!variant) return;
    this.restoringVariantHistory = true;
    this.lastVariantUrl = window.location.href;
    applyVariantOptions(this.root, variant);
    queueMicrotask(() => { this.restoringVariantHistory = false; });
  }

  onToggle(event) {
    const holder = event.target?.closest?.('[data-wq-accordion-root]');
    if (holder) handleAccordionToggle(holder, event);
  }

  onVideoPlay(event) {
    if (event.target?.tagName !== 'VIDEO') return;
    pauseOtherVideos(event.target, [...document.querySelectorAll('[data-wq-product-template] video')]);
  }

  onClick(event) {
    const zoomButton = event.target?.closest?.('.js-photoswipe__zoom');
    if (zoomButton) {
      this.zoomOpener = zoomButton;
      document.documentElement.classList.add('wq-product-zoom-active');
    }
    const playButton = event.target?.closest?.('[data-wq-video-play]');
    if (playButton) this.videoOpener = playButton;
    const modal = event.target?.closest?.('.wq-video-modal');
    if (modal && (event.target === modal || event.target?.closest?.('[data-wq-video-close]'))) {
      queueMicrotask(() => this.restoreVideoFocus());
    }
  }

  onKeydown(event) {
    const modal = event.target?.closest?.('.wq-video-modal');
    if (!modal || modal.getAttribute('aria-hidden') === 'true') return;
    if (event.key === 'Escape') {
      event.preventDefault();
      modal.closest('wq-video-gallery')?.close?.();
      this.restoreVideoFocus();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...modal.querySelectorAll(focusableSelector)].filter((element) => !element.hidden);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  restoreVideoFocus() {
    this.root.querySelectorAll?.('.wq-video-modal video').forEach((video) => video.pause());
    this.videoOpener?.focus?.();
  }

  observePhotoSwipe() {
    const modal = document.querySelector?.('.pswp');
    if (!modal || typeof MutationObserver === 'undefined') return;
    this.photoSwipeObserver = new MutationObserver(() => {
      const open = modal.getAttribute('aria-hidden') === 'false';
      document.documentElement.classList.toggle('wq-product-zoom-active', open && Boolean(this.zoomOpener));
      if (!open && this.zoomOpener) {
        const opener = this.zoomOpener;
        this.zoomOpener = null;
        queueMicrotask(() => restoreZoomFocus(opener));
      }
    });
    this.photoSwipeObserver.observe(modal, { attributes: true, attributeFilter: ['aria-hidden'] });
  }

  observeVideos() {
    if (!('IntersectionObserver' in window)) return;
    this.videoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (!entry.isIntersecting) entry.target.pause?.(); });
    }, { threshold: 0.15 });
    this.root.querySelectorAll?.('video').forEach((video) => this.videoObserver.observe(video));
  }

  destroy() {
    this.abortController.abort();
    this.videoObserver?.disconnect();
    this.productVideoSpacingObserver?.disconnect();
    this.photoSwipeObserver?.disconnect();
    document.documentElement.classList.remove('wq-product-zoom-active');
    this.root.querySelectorAll?.('video').forEach((video) => video.pause?.());
  }
}

const controllerByRoot = new WeakMap();
const accordionControllerByRoot = new WeakMap();

export function initializeWqProductRoot(root) {
  if (!root) return null;
  if (!controllerByRoot.has(root)) controllerByRoot.set(root, new WqProductController(root));
  return controllerByRoot.get(root);
}

export function destroyWqProductRoot(root) {
  controllerByRoot.get(root)?.destroy();
  controllerByRoot.delete(root);
}

export function initializeWqAccordionRoot(root) {
  if (!root || root.matches?.('[data-wq-product-root]') || root.closest?.('[data-wq-product-root]')) return null;
  if (!accordionControllerByRoot.has(root)) {
    const onToggle = (event) => handleAccordionToggle(root, event);
    root.addEventListener?.('toggle', onToggle, { capture: true });
    syncAccordionExpandedState([...root.querySelectorAll('[data-wq-accordion-item]')]);
    accordionControllerByRoot.set(root, {
      destroy() { root.removeEventListener?.('toggle', onToggle, { capture: true }); },
    });
  }
  return accordionControllerByRoot.get(root);
}

export function destroyWqAccordionRoot(root) {
  accordionControllerByRoot.get(root)?.destroy();
  accordionControllerByRoot.delete(root);
}

function initializeWithin(scope = document) {
  const roots = new Set();
  const accordionRoots = new Set();
  if (scope.matches?.('[data-wq-product-root]')) roots.add(scope);
  if (scope.matches?.('[data-wq-accordion-root]')) accordionRoots.add(scope);
  scope.querySelectorAll?.('[data-wq-product-root]').forEach((root) => roots.add(root));
  scope.querySelectorAll?.('[data-wq-accordion-root]').forEach((root) => accordionRoots.add(root));
  roots.forEach(initializeWqProductRoot);
  accordionRoots.forEach(initializeWqAccordionRoot);
}

if (typeof document !== 'undefined') {
  if (typeof customElements !== 'undefined' && !customElements.get('wq-product-recommendations')) {
    customElements.define('wq-product-recommendations', WqProductRecommendations);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeWithin(), { once: true });
  else initializeWithin();
  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target?.matches?.('[data-wq-product-root]')) destroyWqProductRoot(event.target);
    event.target?.querySelectorAll?.('[data-wq-product-root]').forEach(destroyWqProductRoot);
    if (event.target?.matches?.('[data-wq-accordion-root]')) destroyWqAccordionRoot(event.target);
    event.target?.querySelectorAll?.('[data-wq-accordion-root]').forEach(destroyWqAccordionRoot);
  });
}
